import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';

const STATUS_STYLE = { pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', rejected: 'bg-rose-100 text-rose-800' };

export default function AdminKyc() {
    const [tab, setTab] = useState('pending');
    const [items, setItems] = useState([]);
    const [notes, setNotes] = useState({});

    const load = async () => {
        const { data } = await api.get('/admin/kyc', { params: { status: tab } });
        setItems(data || []);
    };
    useEffect(() => { load(); }, [tab]);

    const review = async (id, approve) => {
        try {
            await api.post(`/admin/kyc/${id}/review`, { approve, notes: notes[id] || null });
            toast.success(approve ? 'Solicitud aprobada' : 'Solicitud rechazada');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    };

    return (
        <div className="space-y-4" data-testid="admin-kyc-page">
            <div className="flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-red-600" /><h1 className="text-2xl font-semibold">Verificación KYC</h1></div>
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList>
                    <TabsTrigger value="pending">En revisión</TabsTrigger>
                    <TabsTrigger value="approved">Aprobadas</TabsTrigger>
                    <TabsTrigger value="rejected">Rechazadas</TabsTrigger>
                </TabsList>
                <TabsContent value={tab} className="mt-4 space-y-3">
                    {items.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">Sin solicitudes</div>}
                    {items.map(k => (
                        <Card key={k.id} data-testid={`admin-kyc-${k.id}`}>
                            <CardContent className="p-4">
                                <div className="flex flex-wrap gap-4 justify-between items-start">
                                    <div>
                                        <div className="text-sm text-muted-foreground">@{k.user?.nickname} — {k.full_legal_name}</div>
                                        <div className="text-xs">Documento: <strong>{k.document_type.toUpperCase()}</strong> #{k.document_number}</div>
                                        <div className="text-xs">País: {k.country} • RFC: {k.tax_id || '—'}</div>
                                        {k.address && <div className="text-xs text-muted-foreground">{k.address}</div>}
                                    </div>
                                    <Badge className={STATUS_STYLE[k.status]}>{k.status}</Badge>
                                </div>
                                <div className="grid grid-cols-3 gap-2 mt-3">
                                    {[['Frente','document_front_url'],['Reverso','document_back_url'],['Selfie','selfie_url']].map(([lbl, key]) => k[key] ? (
                                        <a key={key} href={k[key]} target="_blank" rel="noreferrer" className="block">
                                            <div className="text-xs uppercase text-muted-foreground mb-1">{lbl}</div>
                                            <img src={k[key]} alt={lbl} className="w-full h-28 object-cover rounded-md border" />
                                        </a>
                                    ) : null)}
                                </div>
                                {k.status === 'pending' && (
                                    <div className="mt-3 space-y-2">
                                        <Textarea placeholder="Notas al vendedor (opcional)" value={notes[k.id] || ''} onChange={e => setNotes(n => ({...n, [k.id]: e.target.value}))} rows={2} data-testid={`kyc-notes-${k.id}`} />
                                        <div className="flex gap-2">
                                            <Button size="sm" onClick={() => review(k.id, true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`kyc-approve-${k.id}`}><CheckCircle2 className="h-4 w-4 mr-1" />Aprobar</Button>
                                            <Button size="sm" variant="outline" onClick={() => review(k.id, false)} data-testid={`kyc-reject-${k.id}`}><XCircle className="h-4 w-4 mr-1" />Rechazar</Button>
                                        </div>
                                    </div>
                                )}
                                {k.reviewer_notes && <div className="mt-2 text-xs bg-secondary p-2 rounded">Notas: {k.reviewer_notes}</div>}
                            </CardContent>
                        </Card>
                    ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}
